<template>
  <div class="arc-panel">
    <div v-if="loading" class="arc-p-empty">{{ $t('ARC_MGMT.LOADING') }}</div>
    <div v-else-if="error" class="arc-p-empty">
      <p>{{ $t('ARC_PANEL.ERROR') }}</p>
      <button class="pbtn" type="button" @click="load">{{ $t('ARC_PANEL.RETRY') }}</button>
    </div>
    <template v-else>
      <header class="arc-p-head">
        <div>
          <h1>
            {{ $t('ARC_PANEL.TITLE') }}
            <span class="pill ok"><span class="dot" />{{ $t('ARC_MGMT.MONTH_OF', { n: overview.journey.mes_atual }) }} · {{ overview.journey.fase.nome }}</span>
          </h1>
          <p class="sub">{{ $t('ARC_PANEL.SUBTITLE') }}</p>
        </div>
        <div v-if="overview.journey.consultor" class="consultor">
          <span class="avatar">{{ initial(overview.journey.consultor.nome) }}</span>
          <div>
            <small>{{ $t('ARC_PANEL.CONSULTANT') }}</small>
            <b>{{ overview.journey.consultor.nome }}</b>
          </div>
        </div>
      </header>

      <nav class="arc-p-tabs" role="tablist">
        <button v-for="t in tabs" :key="t.key" class="ptab" :class="{ active: activeTab === t.key }" type="button" @click="activeTab = t.key">
          {{ $t('ARC_PANEL.TABS.' + t.key) }}
          <span v-if="t.key === 'BADGES'" class="tag">{{ $t('ARC_PANEL.STAGE2') }}</span>
        </button>
      </nav>

      <section class="arc-p-body">
        <arc-panel-overview v-if="activeTab === 'OVERVIEW'" :overview="overview" @goto-month="gotoMonth" @refresh="load" />
        <arc-panel-deliverables v-else-if="activeTab === 'DELIVERABLES'" :initial-month="focusMonth" @refresh="load" />
        <arc-panel-indicators v-else-if="activeTab === 'INDICATORS'" />
        <arc-panel-governance v-else-if="activeTab === 'GOVERNANCE'" />
        <arc-panel-badges v-else-if="activeTab === 'BADGES'" />
      </section>
    </template>
  </div>
</template>

<script>
import ArcAPI from 'dashboard/api/arc';
import ArcPanelOverview from './Overview.vue';
import ArcPanelDeliverables from './Deliverables.vue';
import ArcPanelIndicators from './Indicators.vue';
import ArcPanelGovernance from './Governance.vue';
import ArcPanelBadges from './Badges.vue';

export default {
  components: { ArcPanelOverview, ArcPanelDeliverables, ArcPanelIndicators, ArcPanelGovernance, ArcPanelBadges },
  data() {
    return {
      loading: true, error: false, overview: {}, activeTab: 'OVERVIEW', focusMonth: null,
      tabs: [
        { key: 'OVERVIEW' }, { key: 'DELIVERABLES' }, { key: 'INDICATORS' },
        { key: 'GOVERNANCE' }, { key: 'BADGES' },
      ],
    };
  },
  mounted() { this.load(); },
  methods: {
    async load() {
      this.loading = true; this.error = false;
      try {
        const { data } = await ArcAPI.overview();
        this.overview = data;
      } catch (e) {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    initial(name) { return (name || '?').trim().charAt(0).toUpperCase(); },
    gotoMonth(mes) { this.focusMonth = mes; this.activeTab = 'DELIVERABLES'; },
  },
};
</script>

<style lang="scss" scoped>
.arc-panel {
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  overflow-y: auto;
  background: var(--s-25);
  padding: var(--space-normal) var(--space-large) var(--space-larger);
  font-family: Inter, sans-serif;
}
.arc-p-empty { padding: var(--space-larger); text-align: center; color: var(--s-500);
  .pbtn { margin-top: var(--space-normal); } }
.arc-p-head { display: flex; justify-content: space-between; align-items: flex-start; gap: var(--space-normal); flex-wrap: wrap; margin-bottom: var(--space-normal);
  h1 { font-size: 21px; font-weight: var(--font-weight-bold); color: var(--s-900); margin: 0; display: flex; align-items: center; gap: var(--space-small); flex-wrap: wrap; }
  .sub { color: var(--s-500); font-size: var(--font-size-small); margin: var(--space-micro) 0 0; } }
.pill { display: inline-flex; align-items: center; gap: var(--space-micro); border-radius: 9999px; padding: 3px 10px; font-size: var(--font-size-mini); font-weight: var(--font-weight-medium);
  &.ok { background: var(--g-100); color: var(--g-800); } }
.dot { width: 7px; height: 7px; border-radius: 50%; background: currentColor; display: inline-block; }
.consultor { display: flex; align-items: center; gap: var(--space-small);
  small { display: block; color: var(--s-400); font-size: var(--font-size-mini); } b { color: var(--s-800); font-size: var(--font-size-small); } }
.avatar { width: 34px; height: 34px; border-radius: 50%; background: #F08A3C; color: #fff; display: grid; place-items: center; font-weight: var(--font-weight-bold); }
.arc-p-tabs { display: flex; gap: 2px; border-bottom: 1px solid var(--s-100); overflow-x: auto; overflow-y: hidden; margin-bottom: var(--space-normal); scrollbar-width: none; }
.arc-p-tabs::-webkit-scrollbar { display: none; }
.ptab { border: 0; background: none; padding: 10px 14px; font-weight: var(--font-weight-medium); font-size: var(--font-size-small); color: var(--s-500); border-bottom: 2px solid transparent; border-radius: 8px 8px 0 0; margin-bottom: -1px; white-space: nowrap; cursor: pointer; display: flex; gap: 6px; align-items: center; transition: color .15s ease, background .15s ease, border-color .15s ease;
  &:hover { color: var(--s-800); background: var(--s-25); }
  &.active { color: var(--s-900); border-bottom-color: var(--w-500); } }
.tag { font-size: 9.5px; font-weight: var(--font-weight-bold); background: var(--s-100); color: var(--s-500); border-radius: 6px; padding: 1px 5px; }
.pbtn { height: var(--space-large); padding: 0 var(--space-normal); border-radius: var(--border-radius-normal); border: 1px solid var(--s-200); background: var(--white); color: var(--s-700); cursor: pointer; }
</style>
